import { useNavigate } from "react-router-dom";

import { Button } from "@stellar/design-system";

import { PausedActionTooltip } from "@/components/PausedActionTooltip";

import { MULTISIG_FEATURE_FLAGS } from "@/constants/multisigFeatureFlags";
import { Routes } from "@/constants/settings";

import { useCoSigningStatus } from "@/apiQueries/multisigEnrollment";
import { useUsers } from "@/apiQueries/useUsers";

import {
  buildCoSigningCopy,
  deriveCoSigningView,
  truncatePublicKey,
} from "@/helpers/coSigningView";

import { useCoSigningPause } from "@/hooks/useCoSigningPause";
import { useIsMultisigFeatureEnabled } from "@/hooks/useIsMultisigFeatureEnabled";
import { useRedux } from "@/hooks/useRedux";

import { CardWithToggler } from "./CardWithToggler";
import { SettingsBanner } from "./SettingsBanner";

/** The amber "Paused" badge that replaces the green "On" while a removal is pending. */
const PausedLabel = () => (
  <div>
    Co-signing is{" "}
    <span
      className="SettingsCoSignerSetup__pausedBadge"
      style={{
        backgroundColor: "var(--sds-clr-amber-03)",
        color: "var(--sds-clr-amber-11)",
        borderRadius: "10px",
        padding: "4px 10px",
      }}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M9 5v14M15 5v14" />
      </svg>{" "}
      PAUSED
    </span>
  </div>
);

export const SettingsCoSignerSetup = () => {
  const navigate = useNavigate();
  const { organization, profile } = useRedux("organization", "profile");
  const isOptOutEnabled = useIsMultisigFeatureEnabled(MULTISIG_FEATURE_FLAGS.OPT_OUT);
  const { data: status, isLoading } = useCoSigningStatus({
    userId: profile.data.id,
    role: profile.data.role ?? null,
  });
  const { isPaused, pause } = useCoSigningPause();
  // Already fetched for Owners by the status query; used to name the inviter.
  const { data: users } = useUsers({ enabled: profile.data.role === "owner" });

  const view = deriveCoSigningView(status);
  if (view === "NOT_APPLICABLE") return null;

  const copy = buildCoSigningCopy(view, status, organization.data.name);
  const isFinancialController = status?.me.role === "financial_controller";
  const isActive = view === "ACTIVE";
  // While a removal is pending, the card is the pause's status line. Only the
  // Owner who started it keeps the Manage button, which is their way back to
  // the review; everyone else is pointed there by the banner above the card.
  const showPaused = isActive && isPaused;
  const hideManage = showPaused && !pause?.isInitiator;

  const handleAction = () => {
    if (view === "ACTIVE" && status?.me.role === "owner") {
      navigate(isOptOutEnabled ? Routes.MULTISIG_MANAGE : `${Routes.SETTINGS}?tab=tm`);
      return;
    }
    if (view === "PENDING_OTHERS") {
      navigate(`${Routes.MULTISIG_SETUP}?edit=1`);
      return;
    }
    navigate(Routes.MULTISIG_SETUP);
  };

  const myPublicKey = status?.me.stellar_public_key;
  const showKeyBlock = isActive && !!myPublicKey;

  // An invitation is a call to action, so it uses the same banner as the
  // other multisig notices above the cards rather than a highlighted card.
  if (view === "MY_TURN" && copy.actionLabel) {
    const invitedBy =
      status?.me?.invited_by ?? users?.find((u) => u.id === status?.enrollment?.initiator_user_id);
    const inviter = invitedBy
      ? `${invitedBy.first_name} ${invitedBy.last_name}`.trim() || "Owner 1"
      : "Owner 1";
    return (
      <SettingsBanner
        variant="warning"
        title={`${inviter} invited you to be a co-signer`}
        actions={[{ label: copy.actionLabel, onClick: handleAction }]}
        className="CoSignerInviteBanner"
      >
        {copy.note}
      </SettingsBanner>
    );
  }

  return (
    <CardWithToggler
      variant="action"
      toggleId="cosigner-setup"
      label={showPaused ? <PausedLabel /> : copy.label}
      note={showPaused ? null : copy.note}
      actionLabel={hideManage ? undefined : copy.actionLabel}
      onAction={copy.actionLabel && !hideManage ? handleAction : undefined}
      actionButtonVariant={copy.actionVariant ?? "tertiary"}
      isPending={isLoading}
    >
      {showKeyBlock ? (
        <div className="SdpSettings__row" style={{ borderTop: "1px solid #dbdbdb" }}>
          <div className="SdpSettings__item" style={{ paddingTop: "10px" }}>
            <label className="SdpSettings__label" htmlFor="cosigner-key">
              Your co-signing key
            </label>
            {isFinancialController ? (
              <PausedActionTooltip isPaused={isPaused}>
                <Button
                  id="cosigner-key-change"
                  variant="tertiary"
                  size="sm"
                  disabled={isPaused}
                  onClick={() => navigate(Routes.MULTISIG_SETUP)}
                >
                  Change key
                </Button>
              </PausedActionTooltip>
            ) : null}
          </div>
          <div className="Note" style={{ width: "75%" }} title={myPublicKey}>
            <span>Disbursement signing key </span>
            <code className="MultisigConnect__code" title={myPublicKey}>
              {" "}
              {truncatePublicKey(myPublicKey)}
            </code>
          </div>
        </div>
      ) : null}
    </CardWithToggler>
  );
};
